import type { Frame } from "@/types/dragon-tiles";

/**
 * Single source of the UI cap on variations. The data model itself is
 * N-capable — only this constant limits how many variations the UI allows.
 * Capped at 26 because `variationLetter` maps index → A..Z and only stays a
 * letter through index 25 (Z).
 */
export const MAX_VARIATIONS = 26;

export type WinningMetric = "open_rate" | "click_rate" | "placed_order_rate";

export type TestDurationUnit = "Hours" | "Days";

/** Minimum allowed A/B test duration, in hours. */
export const MIN_TEST_DURATION_HOURS = 4;

/**
 * True when a test duration meets the 4-hour minimum. Parallels
 * `convertTestDurationToHours` in createBroadcastPayload.ts (Days × 24).
 */
export function isTestDurationValid(
  value: number,
  unit: TestDurationUnit,
): boolean {
  const hours = unit === "Days" ? value * 24 : value;
  return Number.isFinite(hours) && hours >= MIN_TEST_DURATION_HOURS;
}

/** Email-specific authoring payload carried by a variation. */
export interface EmailVariationContent {
  fromName: string;
  fromEmail: string;
  replyTo: string;
  /** Send-time snapshot, only populated on sent broadcasts (FOG-1871). */
  subjectLine: string;
  preheader: string;
  frameId: string | null;
  selectedFrame: Frame | null;
}

/**
 * One variation in the ordered list. Identified by a stable client `id`
 * (never "A"/"B"). Email variations carry an `email` payload; SMS variations
 * carry their authoring state in a sibling MessageDraftStore registry keyed
 * by `id` (see useVariationDraftStores), so only the id/name/frameId live here.
 */
export interface VariationDraft {
  id: string;
  /** Label fallback when no frame name is available ("Variation A"). */
  name: string;
  /** Present for email broadcasts. */
  email?: EmailVariationContent;
  /** Frame id once known. SMS: null until save; email: mirrors email.frameId. */
  frameId: string | null;
}

/** A/B config — only meaningful (and only emitted) when variations.length > 1. */
export interface AbTestConfig {
  winningMetric: WinningMetric;
  testSizePercent: number;
  testDurationValue: number;
  testDurationUnit: TestDurationUnit;
}

export const DEFAULT_AB_TEST_CONFIG: AbTestConfig = {
  winningMetric: "open_rate",
  testSizePercent: 20,
  testDurationValue: 4,
  testDurationUnit: "Hours",
};

export const DEFAULT_EMAIL_VARIATION_CONTENT: EmailVariationContent = {
  fromName: "",
  fromEmail: "",
  replyTo: "",
  subjectLine: "",
  preheader: "",
  frameId: null,
  selectedFrame: null,
};

/** Stable client id for a variation. */
export function createVariationId(): string {
  return crypto.randomUUID();
}

/** Display letter for a 0-based variation index (0 → "A", 1 → "B", ...). */
export function variationLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

/** User-facing default label for a variation by 0-based index ("Variation A"). */
export function variationLabel(index: number): string {
  return `Variation ${variationLetter(index)}`;
}

/** SMS frame / tab label: "MyBroadcast (A)" when multi-variation, plain name otherwise. */
export function smsVariationFrameName(
  broadcastName: string,
  index: number,
  variationCount: number,
): string {
  if (variationCount <= 1) return broadcastName;
  return `${broadcastName} (${variationLetter(index)})`;
}

/** Build an email variation draft, optionally seeding the email content. */
export function makeEmailVariation(
  email: Partial<EmailVariationContent> = {},
  name = "",
): VariationDraft {
  const content: EmailVariationContent = {
    ...DEFAULT_EMAIL_VARIATION_CONTENT,
    ...email,
  };
  return {
    id: createVariationId(),
    name,
    email: content,
    frameId: content.frameId,
  };
}

/**
 * Build an SMS variation draft. SMS variations carry no inline `email`
 * payload — their authoring state lives in a sibling MessageDraftStore
 * registry keyed by `id` (see useVariationDraftStores). `frameId` is null
 * until frames are created at save, or seeded from the wire in edit mode.
 */
export function makeSmsVariation(
  name = "",
  frameId: string | null = null,
): VariationDraft {
  return {
    id: createVariationId(),
    name,
    frameId,
  };
}

/** True when a broadcast has more than one variation (i.e. A/B is "on"). */
export function isAbEnabled(variations: VariationDraft[]): boolean {
  return variations.length > 1;
}
